"use client";

import { useState } from "react";
import Link from "next/link";
import { nakshatras, rashis, type Nakshatra, type NakshatraId } from "@ishvaram/domain-data";
import {
  calculateNaamMatching,
  type NaamConfirmedPerson,
  type NaamMatchingResponse,
} from "@/lib/api/matching-naam";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";
import KundaliMatchCardGenerator from "@/components/KundaliMatchCardGenerator";

/**
 * Quick Ashtakoot check from two Moon rashi + nakshatra picks.
 *
 * The score comes from the backend Gun Milan engine (`/matching/naam-akshar`,
 * the same `calculate_gun_milan` the birth-details calculator above uses), never
 * from arithmetic in this file. Until 2026-10-28 the studio carried its own
 * koota formulas, which disagreed with the engine (Vrishchik/Jyeshtha with
 * Makar/Dhanishta: 29/36 here vs 26/36 above) and could never score Yoni below
 * 2 or Graha Maitri below 3. tests/ashtakoot-milan-studio.test.tsx pins the
 * rendered kootas to recorded engine output.
 *
 * The endpoint takes a (nakshatra, pada) cell and re-derives the rashi, so the
 * nakshatra list is filtered to the nakshatras that fall in the chosen rashi
 * and the first pada inside that rashi is sent. Every Ashtakoot koota depends
 * only on the Moon rashi and nakshatra, so any pada in the same rashi scores
 * the same.
 */

const PADAS_PER_NAKSHATRA = 4;
const PADAS_PER_RASHI = 9;

const RASHIS = [...rashis].sort((a, b) => a.number - b.number);
const NAKSHATRAS = [...nakshatras].sort((a, b) => a.number - b.number);

/**
 * 0-based rashi index owning a (nakshatra, pada) cell — integer arithmetic, as the engine does.
 * @internal
 */
export function padaRashiIndex(nakshatraNumber: number, pada: number): number {
  const absolutePada = (nakshatraNumber - 1) * PADAS_PER_NAKSHATRA + pada;
  return Math.floor((absolutePada - 1) / PADAS_PER_RASHI);
}

/** @internal */
export interface StudioCell {
  nakshatra: Nakshatra;
  /** First pada of this nakshatra that falls in the rashi. */
  pada: number;
}

/**
 * The nakshatras a Moon in this rashi can occupy, each with its first pada there.
 * @internal
 */
export function cellsForRashi(rashiIndex: number): StudioCell[] {
  return NAKSHATRAS.flatMap((nakshatra) => {
    for (let pada = 1; pada <= PADAS_PER_NAKSHATRA; pada += 1) {
      if (padaRashiIndex(nakshatra.number, pada) === rashiIndex) return [{ nakshatra, pada }];
    }
    return [];
  });
}

interface PersonPick {
  rashiIndex: number;
  nakshatraId: NakshatraId;
}

function confirmedCell(pick: PersonPick): NaamConfirmedPerson {
  const cell = cellsForRashi(pick.rashiIndex).find((c) => c.nakshatra.id === pick.nakshatraId);
  if (!cell) throw new Error(`nakshatra ${pick.nakshatraId} is not in rashi ${pick.rashiIndex}`);
  return { moon_nakshatra: cell.nakshatra.id, pada: cell.pada };
}

function rashiLabel(index: number, isHindi: boolean): string {
  const rashi = RASHIS[index];
  return isHindi ? rashi.name_hi : `${rashi.name_en} (${rashi.name_sa})`;
}

type StudioState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "done"; result: NaamMatchingResponse };

export default function AshtakootMilanStudio({ isHindi = false }: { isHindi?: boolean }) {
  const [groom, setGroom] = useState<PersonPick>({ rashiIndex: 0, nakshatraId: "ashwini" });
  const [bride, setBride] = useState<PersonPick>({ rashiIndex: 4, nakshatraId: "magha" });
  const [state, setState] = useState<StudioState>({ status: "idle" });

  // A changed pick invalidates the shown score; the seeker re-runs it.
  const updateGroom = (next: PersonPick) => {
    setGroom(next);
    setState({ status: "idle" });
  };
  const updateBride = (next: PersonPick) => {
    setBride(next);
    setState({ status: "idle" });
  };

  const handleCalculate = async () => {
    setState({ status: "loading" });
    try {
      const result = await calculateNaamMatching(
        confirmedCell(groom),
        confirmedCell(bride),
        isHindi ? "hi" : "en"
      );
      setState({ status: "done", result });
    } catch {
      setState({ status: "error" });
    }
  };

  const total = state.status === "done" ? state.result.total_score : null;

  const handleConsult = () => {
    safeSessionSet(
      "ishvaram_intent_context",
      JSON.stringify({
        source: "/kundli-matching",
        groom: RASHIS[groom.rashiIndex].name_en,
        bride: RASHIS[bride.rashiIndex].name_en,
        score: total,
      })
    );
  };

  const chatHref = buildSEOAgentHref(
    getSEOIntentConfigForSource("/kundli-matching"),
    "/kundli-matching",
    isHindi ? "hi" : "en"
  );
  const groomRashi = rashiLabel(groom.rashiIndex, isHindi);
  const brideRashi = rashiLabel(bride.rashiIndex, isHindi);
  const whatsappShareUrl = shareUrl(isHindi, total, groomRashi, brideRashi);

  return (
    <div className="my-8 rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-7 shadow-xs">
      <StudioHeader isHindi={isHindi} whatsappShareUrl={whatsappShareUrl} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
        <PersonSelector
          title={isHindi ? "वर (Groom) का विवरण" : "Groom (Var) Details"}
          icon="🤵"
          isHindi={isHindi}
          pick={groom}
          onChange={updateGroom}
          idPrefix={isHindi ? "studio-hi-groom" : "studio-groom"}
        />
        <PersonSelector
          title={isHindi ? "वधू (Bride) का विवरण" : "Bride (Kanya) Details"}
          icon="👰"
          isHindi={isHindi}
          pick={bride}
          onChange={updateBride}
          idPrefix={isHindi ? "studio-hi-bride" : "studio-bride"}
        />
      </div>

      <CalculateControls isHindi={isHindi} status={state.status} onCalculate={handleCalculate} />

      {state.status === "done" ? (
        <>
          <ResultCard
            isHindi={isHindi}
            result={state.result}
            chatHref={chatHref}
            onConsult={handleConsult}
          />
          <KundaliMatchCardGenerator
            groomName={isHindi ? "वर" : "Groom"}
            brideName={isHindi ? "वधू" : "Bride"}
            groomRashi={groomRashi}
            brideRashi={brideRashi}
            score={state.result.total_score}
            isHindi={isHindi}
          />
        </>
      ) : null}
    </div>
  );
}

function shareUrl(
  isHindi: boolean,
  total: number | null,
  groomRashi: string,
  brideRashi: string
): string {
  let text: string;
  if (total === null) {
    text = isHindi
      ? "वैदिक 36 गुण मिलान Ishvaram पर निःशुल्क देखें। https://ishvaram.com/hi/kundli-matching/"
      : "Check the Vedic 36-Gun Milan score free on Ishvaram. https://ishvaram.com/kundli-matching/";
  } else {
    text = isHindi
      ? `वैदिक 36 गुण मिलान: ${groomRashi} और ${brideRashi} के बीच ${total}/36 गुण, Ishvaram पर। https://ishvaram.com/hi/kundli-matching/`
      : `Vedic 36-Gun Milan Score: ${total}/36 between ${groomRashi} & ${brideRashi} on Ishvaram. https://ishvaram.com/kundli-matching/`;
  }
  return `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
}

const CONTROLS_COPY = {
  en: {
    calculate: "Calculate Gun Milan",
    loading: "Calculating…",
    note: "Scored by the same Ashtakoot engine as the calculator above. Mangal Dosh needs both full kundalis — enter birth details above for it.",
    error: "The score could not be loaded just now. Please try again in a moment.",
  },
  hi: {
    calculate: "36 गुण मिलान देखें",
    loading: "गुण गिने जा रहे हैं…",
    note: "स्कोर उसी अष्टकूट इंजन से आता है जो ऊपर का कैलकुलेटर चलाता है। मांगलिक दोष के लिए दोनों की पूरी कुंडली चाहिए — ऊपर जन्म विवरण भरें।",
    error: "स्कोर अभी नहीं मिल सका। कृपया थोड़ी देर बाद फिर कोशिश करें।",
  },
} as const;

function CalculateControls({
  isHindi,
  status,
  onCalculate,
}: {
  isHindi: boolean;
  status: StudioState["status"];
  onCalculate: () => void;
}) {
  const copy = CONTROLS_COPY[isHindi ? "hi" : "en"];
  return (
    <div className="mb-6 flex flex-col items-center gap-2">
      <button
        type="button"
        onClick={onCalculate}
        disabled={status === "loading"}
        className="inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-6 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm disabled:opacity-60"
      >
        {status === "loading" ? copy.loading : copy.calculate}
      </button>
      <p className="text-caption text-text-muted text-center">{copy.note}</p>
      {status === "error" ? (
        <p role="alert" className="text-caption text-intent-error text-center">
          {copy.error}
        </p>
      ) : null}
    </div>
  );
}

function StudioHeader({
  isHindi,
  whatsappShareUrl,
}: {
  isHindi: boolean;
  whatsappShareUrl: string;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-border-default">
      <div>
        <span className="text-caption font-bold uppercase tracking-wider text-intent-brand">
          {isHindi ? "36 गुण मिलान सॉफ्टवेयर" : "36-Point Gun Milan Software"}
        </span>
        <h2 className="text-2xl font-bold font-serif text-text-primary mt-1">
          {isHindi
            ? "अष्टकूट कुंडली मिलान व नाड़ी दोष कैलकुलेटर"
            : "Ashtakoot Kundli Matching & Nadi Dosha Calculator"}
        </h2>
      </div>
      <a
        href={whatsappShareUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-lg border border-border-default bg-surface-raised px-3.5 py-2 text-caption font-bold text-text-primary hover:bg-surface-warm transition-colors shrink-0"
      >
        <span>📱</span>
        <span>{isHindi ? "WhatsApp पर शेयर करें" : "Share on WhatsApp"}</span>
      </a>
    </div>
  );
}

function PersonSelector({
  title,
  icon,
  isHindi,
  pick,
  onChange,
  idPrefix,
}: {
  title: string;
  icon: string;
  isHindi: boolean;
  pick: PersonPick;
  onChange: (next: PersonPick) => void;
  idPrefix: string;
}) {
  const cells = cellsForRashi(pick.rashiIndex);
  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-4">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border-default">
        <span className="text-xl">{icon}</span>
        <h3 className="text-body font-bold text-text-primary">{title}</h3>
      </div>
      <div className="space-y-3">
        <div>
          <label
            htmlFor={`${idPrefix}-rashi`}
            className="text-caption font-semibold text-text-secondary block mb-1"
          >
            {isHindi ? "चंद्र राशि" : "Moon Rashi"}
          </label>
          <select
            id={`${idPrefix}-rashi`}
            value={pick.rashiIndex}
            onChange={(e) => {
              const rashiIndex = Number(e.target.value);
              onChange({ rashiIndex, nakshatraId: cellsForRashi(rashiIndex)[0].nakshatra.id });
            }}
            className="w-full rounded-lg border border-border-default bg-surface-raised px-3 py-2 text-body-small text-text-primary font-medium focus:border-intent-brand focus:outline-none"
          >
            {RASHIS.map((r, i) => (
              <option key={r.id} value={i}>
                {rashiLabel(i, isHindi)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor={`${idPrefix}-nakshatra`}
            className="text-caption font-semibold text-text-secondary block mb-1"
          >
            {isHindi ? "जन्म नक्षत्र" : "Birth Nakshatra"}
          </label>
          <select
            id={`${idPrefix}-nakshatra`}
            value={pick.nakshatraId}
            onChange={(e) =>
              onChange({ rashiIndex: pick.rashiIndex, nakshatraId: e.target.value as NakshatraId })
            }
            className="w-full rounded-lg border border-border-default bg-surface-raised px-3 py-2 text-body-small text-text-primary font-medium focus:border-intent-brand focus:outline-none"
          >
            {cells.map(({ nakshatra }) => (
              <option key={nakshatra.id} value={nakshatra.id}>
                {isHindi ? nakshatra.name_hi : nakshatra.name_en}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

function barWidth(ratio: number): string {
  if (ratio >= 1) return "w-full";
  if (ratio >= 0.7) return "w-3/4";
  if (ratio >= 0.4) return "w-1/2";
  if (ratio > 0) return "w-1/4";
  return "w-0";
}

function ResultCard({
  isHindi,
  result,
  chatHref,
  onConsult,
}: {
  isHindi: boolean;
  result: NaamMatchingResponse;
  chatHref: string;
  onConsult: () => void;
}) {
  const nadi = result.kootas.find((k) => k.name === "Nadi");
  const nadiDosha = nadi !== undefined && nadi.score === 0;
  return (
    <div
      className="rounded-xl border border-border-default bg-surface-raised p-5"
      data-studio-result
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border-default">
        <div>
          <span className="text-caption font-bold uppercase tracking-wider text-text-muted">
            {isHindi ? "कुल प्राप्त अंक" : "Total Score"}
          </span>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-4xl font-serif font-black text-text-primary" data-studio-total>
              {result.total_score}
            </span>
            <span className="text-lg font-semibold text-text-muted">
              / {result.max_score} {isHindi ? "गुण" : "Gunas"}
            </span>
          </div>
        </div>
        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          {isHindi ? "विवाह विशेषज्ञ से पूछें ✦" : "Analyze Compatibility with AI ✦"}
        </a>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {result.kootas.map((koota) => (
          <div
            key={koota.name}
            className="p-3 rounded-lg border border-border-default bg-surface-raised"
            data-studio-koota={koota.name}
            title={isHindi ? undefined : koota.description}
          >
            <div className="flex items-center justify-between text-caption font-bold mb-1">
              <span className="text-text-primary truncate">
                {isHindi ? koota.name_hi : koota.name}
              </span>
              <span className="text-intent-brand font-mono shrink-0 ml-1" data-studio-koota-score>
                {koota.score} / {koota.max}
              </span>
            </div>
            <div className="w-full bg-surface-warm rounded-full h-1.5 overflow-hidden">
              <div
                className={`bg-intent-brand h-full rounded-full transition-all duration-300 ${barWidth(
                  koota.score / koota.max
                )}`}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <span className="text-text-secondary">
          {nadiDosha
            ? isHindi
              ? "⚠️ दोनों की नाड़ी एक है (नाड़ी 0/8) — पूरी कुंडली में नाड़ी दोष परिहार की जांच कराएं।"
              : "⚠️ Same Nadi (Nadi 0/8) — check the full kundalis for Nadi Dosh Bhang."
            : isHindi
              ? "✓ नाड़ी अलग है — नाड़ी दोष नहीं।"
              : "✓ Different Nadi — no Nadi Dosha."}
        </span>
        <Link
          href={chatHref}
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1 shrink-0"
        >
          {isHindi ? "नाड़ी दोष निवारण परामर्श →" : "Get Nadi Dosha Advice →"}
        </Link>
      </div>
    </div>
  );
}
